import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  lazy,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";
import type { MailboxSummary } from "@asc-mail/contracts";
import type { MailSession } from "../App";
import { absoluteAssetUrl, api } from "../lib/api";
import { applyMailBrand, authenticatedMailLogo } from "../lib/brand";
import { displayMailLabelName } from "../lib/labels";
import { ignoreUnavailableAction } from "../lib/unavailable";
import { avatarColor, initials, cn } from "../lib/utils";
import type { ComposePreset } from "./ComposeWindow";
import { MaterialIcon } from "./MaterialIcon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";

const loadComposeWindow = () => import("./ComposeWindow");
const LazyComposeWindow = lazy(async () => {
  const module = await loadComposeWindow();
  return { default: module.ComposeWindow };
});

interface Counts {
  INBOX: number;
  SENT: number;
  DRAFT: number;
  ARCHIVE: number;
  SPAM: number;
  TRASH: number;
  unread: number;
}
interface LabelDto {
  id: string;
  name: string;
  color: string;
  _count: { placements: number };
}
type CountKey = keyof Counts;

export interface MailShellContextValue {
  session: MailSession;
  mailboxes: MailboxSummary[];
  mailbox: MailboxSummary | null;
  compose: (preset?: ComposePreset) => void;
  closeMobileSidebar: () => void;
}

interface AvailableEntry {
  kind: "available";
  key: string;
  label: string;
  icon: string;
  count?: CountKey;
}
interface UnavailableEntry {
  kind: "unavailable";
  key: string;
  label: string;
  icon: string;
}
type NavEntry = AvailableEntry | UnavailableEntry;

const primaryEntries: NavEntry[] = [
  {
    kind: "available",
    key: "inbox",
    label: "Inbox",
    icon: "inbox",
    count: "unread",
  },
  { kind: "available", key: "starred", label: "Starred", icon: "star" },
  { kind: "unavailable", key: "snoozed", label: "Snoozed", icon: "schedule" },
  {
    kind: "available",
    key: "sent",
    label: "Sent",
    icon: "send",
    count: "SENT",
  },
  {
    kind: "available",
    key: "draft",
    label: "Drafts",
    icon: "draft",
    count: "DRAFT",
  },
];

const moreEntries: NavEntry[] = [
  {
    kind: "available",
    key: "important",
    label: "Important",
    icon: "label_important",
  },
  {
    kind: "unavailable",
    key: "scheduled",
    label: "Scheduled",
    icon: "schedule_send",
  },
  {
    kind: "unavailable",
    key: "all-mail",
    label: "All Mail",
    icon: "stacked_email",
  },
  {
    kind: "available",
    key: "archive",
    label: "Archive",
    icon: "archive",
    count: "ARCHIVE",
  },
  {
    kind: "available",
    key: "spam",
    label: "Spam",
    icon: "report",
    count: "SPAM",
  },
  {
    kind: "available",
    key: "trash",
    label: "Trash",
    icon: "delete",
    count: "TRASH",
  },
];

const MailShellContext = createContext<MailShellContextValue | null>(null);
export const useMailShell = () => {
  const value = useContext(MailShellContext);
  if (!value) throw new Error("MailShellContext missing");
  return value;
};

function applyTheme(theme: MailSession["theme"]) {
  const dark =
    theme === "DARK" ||
    (theme === "SYSTEM" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.classList.toggle("dark", dark);
}

function UnavailableButton({
  icon,
  label,
  compact = false,
}: {
  icon: string;
  label: string;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      className={cn("mail-folder-item", compact && "compact")}
      title={compact ? label : undefined}
      onClick={ignoreUnavailableAction}
    >
      <MaterialIcon>{icon}</MaterialIcon>
      <span>{label}</span>
    </button>
  );
}

function SidebarLabelMenu({ label }: { label: LabelDto }) {
  const unavailable = ignoreUnavailableAction;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="mail-label-more gmail-icon-button small"
          aria-label={`More options for ${displayMailLabelName(label.name)}`}
        >
          <MaterialIcon>more_vert</MaterialIcon>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="right"
        sideOffset={2}
        collisionPadding={8}
        className="gmail-label-menu w-[260px] p-0"
      >
        <DropdownMenuItem onSelect={unavailable}>
          <span
            className="mail-label-menu-swatch"
            style={{ background: label.color }}
          />
          Label color
          <MaterialIcon className="ml-auto">chevron_right</MaterialIcon>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-0" />
        <div className="mail-label-menu-heading">In label list</div>
        <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
          <MaterialIcon>check</MaterialIcon>Show
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={unavailable}>
          <span className="size-6" />
          Show if unread
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={unavailable}>
          <span className="size-6" />
          Hide
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-0" />
        <div className="mail-label-menu-heading">In message list</div>
        <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
          <MaterialIcon>check</MaterialIcon>Show
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={unavailable}>
          <span className="size-6" />
          Hide
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-0" />
        <DropdownMenuItem onSelect={unavailable}>Edit</DropdownMenuItem>
        <DropdownMenuItem onSelect={unavailable}>Remove label</DropdownMenuItem>
        <DropdownMenuItem onSelect={unavailable}>Add sublabel</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function MailShell({ session }: { session: MailSession }) {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [sidebarCompact, setSidebarCompact] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [search, setSearch] = useState(searchParams.get("q") ?? "");
  const [composePreset, setComposePreset] = useState<ComposePreset | null>(
    null,
  );
  const mailboxesQuery = useQuery({
    queryKey: ["mail", "mailboxes"],
    queryFn: () => api<MailboxSummary[]>("/mail/mailboxes"),
  });
  const mailboxes = mailboxesQuery.data ?? [];
  const mailbox =
    mailboxes.find((item) => item.id === params.mailboxId) ??
    mailboxes[0] ??
    null;
  const counts = useQuery({
    queryKey: ["mail", mailbox?.id, "counts"],
    queryFn: () => api<Counts>(`/mail/mailboxes/${mailbox!.id}/counts`),
    enabled: Boolean(mailbox),
  });
  const labels = useQuery({
    queryKey: ["mail", mailbox?.id, "labels"],
    queryFn: () => api<LabelDto[]>(`/mail/mailboxes/${mailbox!.id}/labels`),
    enabled: Boolean(mailbox),
  });
  const visibleLabels = useMemo(() => {
    const byName = new Map<string, LabelDto>();
    for (const label of labels.data ?? []) {
      const key = displayMailLabelName(label.name).toLocaleLowerCase();
      const current = byName.get(key);
      if (!current || label._count.placements > current._count.placements)
        byName.set(key, label);
    }
    return [...byName.values()];
  }, [labels.data]);
  const preference = useMutation({
    mutationFn: (next: Pick<MailSession, "readingPane" | "theme">) =>
      api<MailSession>("/mail/preferences", {
        method: "PUT",
        body: JSON.stringify(next),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["mail", "session"], { ...session, ...data });
      applyTheme(data.theme);
    },
    onError: () => toast.error("Unable to save settings"),
  });
  const logout = useMutation({
    mutationFn: () => api<void>("/auth/mail/logout", { method: "POST" }),
    onSuccess: () => {
      queryClient.clear();
      navigate("/login", { replace: true });
    },
  });

  useEffect(() => {
    applyTheme(session.theme);
    applyMailBrand();
  }, [session.theme]);
  useEffect(() => {
    if (!params.mailboxId && mailboxes[0])
      navigate(`/${mailboxes[0].id}/inbox`, { replace: true });
  }, [mailboxes, navigate, params.mailboxId]);

  const activeFolder = params.folder ?? "inbox";
  const goFolder = (key: string, extra = "") => {
    if (!mailbox) return;
    navigate(`/${mailbox.id}/${key}${extra}`);
    setMobileOpen(false);
  };
  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    if (!mailbox) return;
    navigate(
      `/${mailbox.id}/${activeFolder}${search ? `?q=${encodeURIComponent(search)}` : ""}`,
    );
  };
  const savePreference = (next: Partial<Pick<MailSession, "theme">>) =>
    preference.mutate({
      readingPane: "OFF",
      theme: next.theme ?? session.theme,
    });

  const context = useMemo<MailShellContextValue>(
    () => ({
      session,
      mailboxes,
      mailbox,
      compose: (preset = {}) =>
        setComposePreset({ ...preset, key: crypto.randomUUID() }),
      closeMobileSidebar: () => setMobileOpen(false),
    }),
    [session, mailboxes, mailbox],
  );

  const renderEntry = (entry: NavEntry) => {
    if (entry.kind === "unavailable")
      return (
        <UnavailableButton
          key={entry.key}
          icon={entry.icon}
          label={entry.label}
          compact={sidebarCompact}
        />
      );
    const count = entry.count ? counts.data?.[entry.count] : undefined;
    return (
      <button
        key={entry.key}
        type="button"
        className={cn(
          "mail-folder-item",
          activeFolder === entry.key && "active",
          sidebarCompact && "compact",
        )}
        onClick={() => goFolder(entry.key)}
        title={sidebarCompact ? entry.label : undefined}
      >
        <MaterialIcon
          className={activeFolder === entry.key ? "filled" : undefined}
        >
          {entry.icon}
        </MaterialIcon>
        <span>{entry.label}</span>
        {!sidebarCompact && count ? <strong>{count}</strong> : null}
      </button>
    );
  };

  return (
    <MailShellContext.Provider value={context}>
      <div className="mail-app">
        <header className="mail-topbar">
          <button
            type="button"
            className="gmail-icon-button mail-main-menu-button"
            aria-label={mobileOpen ? "Close navigation" : "Main menu"}
            onClick={() =>
              window.matchMedia("(max-width: 760px)").matches
                ? setMobileOpen((value) => !value)
                : setSidebarCompact((value) => !value)
            }
          >
            <MaterialIcon>menu</MaterialIcon>
          </button>
          <button
            type="button"
            className="mail-brand"
            aria-label={authenticatedMailLogo.accessibleName}
            onClick={() => mailbox && goFolder("inbox")}
          >
            <img
              src={authenticatedMailLogo.logoUrl}
              alt={authenticatedMailLogo.accessibleName}
            />
          </button>
          <form onSubmit={submitSearch} className="mail-search">
            <MaterialIcon className="search-leading">search</MaterialIcon>
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="mail-search-input"
              placeholder="Search mail"
              aria-label="Search mail"
            />
            {search ? (
              <button
                type="button"
                className="search-clear"
                aria-label="Clear search"
                onClick={() => setSearch("")}
              >
                <MaterialIcon>close</MaterialIcon>
              </button>
            ) : null}
            <button
              type="button"
              className="search-options"
              aria-label="Show search options"
              onClick={ignoreUnavailableAction}
            >
              <MaterialIcon>tune</MaterialIcon>
            </button>
          </form>
          <div className="topbar-actions">
            <button
              type="button"
              className="gmail-icon-button"
              aria-label="Help"
              onClick={ignoreUnavailableAction}
            >
              <MaterialIcon>help</MaterialIcon>
            </button>
            <button
              type="button"
              className={cn("gmail-icon-button", settingsOpen && "active")}
              aria-label="Settings"
              onClick={() => setSettingsOpen((value) => !value)}
            >
              <MaterialIcon>settings</MaterialIcon>
            </button>
            <button
              type="button"
              className="gmail-icon-button"
              aria-label="Google apps"
              onClick={ignoreUnavailableAction}
            >
              <MaterialIcon>apps</MaterialIcon>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="account-avatar"
                  aria-label={`Account: ${session.email}`}
                >
                  <span style={{ background: avatarColor(session.email) }}>
                    {initials(session.displayName, session.email)}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="account-popover w-[340px] rounded-[24px] p-3"
              >
                <div className="px-3 py-2 text-center text-sm">
                  {session.email}
                </div>
                <div className="flex flex-col items-center pb-4">
                  <span
                    className="grid size-16 place-items-center rounded-full text-xl font-medium text-white"
                    style={{ background: avatarColor(session.email) }}
                  >
                    {initials(session.displayName, session.email)}
                  </span>
                  <strong className="mt-3 text-xl font-normal">
                    Hi, {session.displayName}
                  </strong>
                </div>
                <DropdownMenuSeparator />
                {mailboxes.map((item) => (
                  <DropdownMenuItem
                    key={item.id}
                    className="min-h-12"
                    onSelect={() => navigate(`/${item.id}/inbox`)}
                  >
                    {item.avatarUrl ? (
                      <img
                        src={absoluteAssetUrl(item.avatarUrl)}
                        alt=""
                        className="size-8 rounded-full object-cover"
                      />
                    ) : (
                      <span
                        className="grid size-8 place-items-center rounded-full text-xs font-bold text-white"
                        style={{ background: avatarColor(item.address) }}
                      >
                        {initials(item.displayName, item.address)}
                      </span>
                    )}
                    <span className="min-w-0">
                      <span className="block truncate">{item.displayName}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {item.address}
                      </span>
                    </span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => logout.mutate()}>
                  <MaterialIcon>logout</MaterialIcon>Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <div className={cn("mail-frame", sidebarCompact && "sidebar-compact")}>
          {mobileOpen ? (
            <button
              type="button"
              aria-label="Close navigation"
              className="mobile-sidebar-scrim"
              onClick={() => setMobileOpen(false)}
            />
          ) : null}
          <aside
            className={cn(
              "mail-sidebar mail-scrollbar",
              mobileOpen && "mobile-open",
            )}
          >
            <button
              type="button"
              className={cn("mail-compose-button", sidebarCompact && "compact")}
              onPointerEnter={() => void loadComposeWindow()}
              onFocus={() => void loadComposeWindow()}
              onClick={() => context.compose()}
            >
              <MaterialIcon>edit</MaterialIcon>
              <span>Compose</span>
            </button>
            <nav className="mail-nav" aria-label="Mail folders">
              {primaryEntries.map(renderEntry)}
              <button
                type="button"
                className={cn("mail-folder-item", sidebarCompact && "compact")}
                onClick={() => setMoreOpen((value) => !value)}
                title={sidebarCompact ? "More" : undefined}
              >
                <MaterialIcon>
                  {moreOpen ? "expand_less" : "expand_more"}
                </MaterialIcon>
                <span>{moreOpen ? "Less" : "More"}</span>
              </button>
              {moreOpen ? moreEntries.map(renderEntry) : null}
            </nav>
            {!sidebarCompact ? (
              <div className="mail-labels">
                <div className="mail-labels-heading">
                  <span>Labels</span>
                  <button
                    type="button"
                    className="gmail-icon-button small"
                    aria-label="Create label"
                    onClick={ignoreUnavailableAction}
                  >
                    <MaterialIcon>add</MaterialIcon>
                  </button>
                </div>
                {visibleLabels.map((label) => (
                  <div className="mail-label-row" key={label.id}>
                    <button
                      type="button"
                      className="mail-label-item"
                      onClick={() => goFolder("inbox", `?labelId=${label.id}`)}
                    >
                      <MaterialIcon className="mail-custom-label-icon filled">
                        label
                      </MaterialIcon>
                      <span>{displayMailLabelName(label.name)}</span>
                      <strong>{label._count.placements || ""}</strong>
                    </button>
                    <SidebarLabelMenu label={label} />
                  </div>
                ))}
              </div>
            ) : null}
          </aside>
          <main className="mail-surface">
            <Outlet />
          </main>
        </div>

        <span className="mail-side-panel-toggle" aria-hidden="true">
          <MaterialIcon>chevron_left</MaterialIcon>
        </span>

        {settingsOpen ? (
          <aside className="quick-settings" aria-label="Quick settings">
            <div className="quick-settings-header">
              <h2>Quick settings</h2>
              <button
                type="button"
                className="gmail-icon-button"
                aria-label="Close settings"
                onClick={() => setSettingsOpen(false)}
              >
                <MaterialIcon>close</MaterialIcon>
              </button>
            </div>
            <section>
              <h3>Theme</h3>
              <div className="theme-options">
                {(["LIGHT", "DARK", "SYSTEM"] as const).map((theme) => (
                  <button
                    type="button"
                    key={theme}
                    className={session.theme === theme ? "active" : ""}
                    onClick={() => savePreference({ theme })}
                  >
                    <MaterialIcon>
                      {theme === "LIGHT"
                        ? "light_mode"
                        : theme === "DARK"
                          ? "dark_mode"
                          : "contrast"}
                    </MaterialIcon>
                    <span>
                      {theme[0]}
                      {theme.slice(1).toLowerCase()}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          </aside>
        ) : null}
        {composePreset ? (
          <Suspense fallback={null}>
            <LazyComposeWindow
              key={composePreset.key}
              preset={composePreset}
              mailbox={mailbox}
              onClose={() => setComposePreset(null)}
            />
          </Suspense>
        ) : null}
      </div>
    </MailShellContext.Provider>
  );
}
